/**
 * One HTTP client for the four AdCrew backends.
 *
 * Every call carries the Bearer token. A 401 signs the user out once and sends
 * them to /login (the old app never handled 401s). Errors come back as
 * ApiError with the DRF body attached, so forms can show field messages.
 */
const ROOT = (import.meta.env.VITE_API_ROOT || 'https://services.adcrew.co/api/v1').replace(/\/$/, '')

export const BASES = {
  auth: `${ROOT}/auth`,
  mgmt: ROOT,
  player: `${ROOT}/players`,
  comm: `${ROOT}/communications`,
  admin: `${ROOT}/admin`,
}

export const PLAYER_JS_URL = import.meta.env.VITE_PLAYER_JS_URL || 'https://player.adcrew.co/player.js'
export const publicPlayerUrl = (id) => `${BASES.player}/public/${id}/`

export class ApiError extends Error {
  constructor(status, body) {
    super(messageFrom(status, body))
    this.status = status
    this.body = body
    // { field: 'first message' } for DRF validation errors
    this.fields = {}
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      for (const [k, v] of Object.entries(body)) {
        if (k !== 'detail' && k !== 'non_field_errors') this.fields[k] = Array.isArray(v) ? String(v[0]) : typeof v === 'string' ? v : JSON.stringify(v)
      }
    }
  }
}

function messageFrom(status, body) {
  if (body && typeof body === 'object') {
    if (body.detail) return String(body.detail)
    if (body.message) return String(body.message)
    if (Array.isArray(body.non_field_errors)) return body.non_field_errors.join(' ')
    const first = Object.entries(body)[0]
    if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : typeof first[1] === 'string' ? first[1] : 'invalid'}`
  }
  if (typeof body === 'string' && body && body.length < 200) return body
  if (status === 0) return 'Could not reach the server.'
  if (status >= 500) return `The server failed (${status}).`
  return `Request failed (${status}).`
}

let onUnauthorized = null
export function setUnauthorizedHandler(fn) { onUnauthorized = fn }

export function token() {
  try { return localStorage.getItem('accessToken') } catch (e) { return null }
}

export function qs(params = {}) {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export async function request(base, path, { method = 'GET', params, body, signal, auth = true } = {}) {
  const url = `${BASES[base] ?? base}${path}${qs(params)}`
  const headers = { Accept: 'application/json' }
  const t = auth && token()
  if (t) headers.Authorization = `Bearer ${t}`
  let payload
  if (body instanceof FormData) payload = body
  else if (body !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body) }

  let res
  try {
    res = await fetch(url, { method, headers, body: payload, signal, credentials: 'include' })
  } catch (e) {
    if (e.name === 'AbortError') throw e
    throw new ApiError(0, null)
  }
  const text = await res.text()
  let data = text
  try { data = text ? JSON.parse(text) : null } catch (e) { /* not JSON */ }
  if (res.status === 401 && t && onUnauthorized) onUnauthorized()
  if (!res.ok) throw new ApiError(res.status, data)
  return data
}

/**
 * POST and read the answer as Server-Sent Events: `onEvent(name, data)` per
 * event. Resolves when the stream ends; a non-2xx answer throws ApiError
 * before any event, like request().
 */
export async function stream(base, path, body, onEvent, { signal } = {}) {
  const headers = { Accept: 'text/event-stream', 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers.Authorization = `Bearer ${t}`
  let res
  try {
    res = await fetch(`${BASES[base] ?? base}${path}`, { method: 'POST', headers, body: JSON.stringify(body), signal })
  } catch (e) {
    if (e.name === 'AbortError') throw e
    throw new ApiError(0, null)
  }
  if (!res.ok) {
    const text = await res.text()
    let data = text
    try { data = text ? JSON.parse(text) : null } catch (e) { /* not JSON */ }
    if (res.status === 401 && t && onUnauthorized) onUnauthorized()
    throw new ApiError(res.status, data)
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let at
    while ((at = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, at)
      buffer = buffer.slice(at + 2)
      let event = 'message'
      const data = []
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7)
        else if (line.startsWith('data: ')) data.push(line.slice(6))
      }
      if (!data.length) continue
      let parsed = data.join('\n')
      try { parsed = JSON.parse(parsed) } catch (e) { /* plain text */ }
      onEvent(event, parsed)
    }
  }
}

/** CRUD on a DRF resource: `resource('mgmt', '/websites/')`. */
export function resource(base, path) {
  const at = (id) => `${path}${id}/`
  return {
    list: (params, opts) => request(base, path, { params, ...opts }),
    get: (id, opts) => request(base, at(id), opts),
    create: (body) => request(base, path, { method: 'POST', body }),
    patch: (id, body) => request(base, at(id), { method: 'PATCH', body }),
    put: (id, body) => request(base, at(id), { method: 'PUT', body }),
    remove: (id) => request(base, at(id), { method: 'DELETE' }),
    options: () => request(base, path, { method: 'OPTIONS' }),
  }
}

/** DRF list -> array whether or not the endpoint paginates. */
export const rows = (data) => (Array.isArray(data) ? data : data?.results ?? [])

/** OPTIONS -> choices for a POST field, as [{label, value}]. */
export function choicesFrom(options, field) {
  const c = options?.actions?.POST?.[field]?.choices ?? []
  return c.map((x) => ({ label: x.display_name ?? String(x.value), value: x.value }))
}
